import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { getDefaultAvatarForTitle } from "../../utils/avatarUtils";
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  Fingerprint,
  Key,
  Loader2,
  Mail,
  Phone,
  Shield,
  Trash2,
  User,
  UserCog
} from "lucide-react";
import "../../styles/UtopiaPages.css";

const GENERIC_AVATAR = "/default-avatar.png";

interface ProfileSettingsProps {
  userName?: string;
  hideWhenSet?: boolean;
}

interface ProfileFormState {
  name: string;
  email: string;
  phone: string;
  title: string;
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ userName: propUserName, hideWhenSet }) => {
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const currentUserId = localStorage.getItem("userId") || "";
  const canChangePassword = role === "admin" || role === "superadmin";
  const showAdminSettings = role === "admin" || role === "superadmin";
  const isSuperAdmin = role === "superadmin";

  const getDefaultAvatar = () => localStorage.getItem("profilePicture") || getDefaultAvatarForTitle(localStorage.getItem("userTitle"));

  const [profilePic, setProfilePic] = useState<string>(getDefaultAvatar);
  const [uploading, setUploading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [profileForm, setProfileForm] = useState<ProfileFormState>({
    name: propUserName || localStorage.getItem("userName") || "User",
    email: localStorage.getItem("email") || "",
    phone: localStorage.getItem("phone") || "",
    title: localStorage.getItem("userTitle") || ""
  });

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwords, setPasswords] = useState({ new: "", confirm: "" });
  const [showPass, setShowPass] = useState(false);
  const [passLoading, setPassLoading] = useState(false);

  useAutoDismiss(message, setMessage, "", 5000);
  useAutoDismiss(error, setError, "", 5000);

  useEffect(() => {
    if (!localStorage.getItem("profilePicture")) {
      setProfilePic(getDefaultAvatarForTitle(localStorage.getItem("userTitle")));
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      setProfilePic(localStorage.getItem("profilePicture") || getDefaultAvatarForTitle(localStorage.getItem("userTitle")));
      setProfileForm({
        name: propUserName || localStorage.getItem("userName") || "User",
        email: localStorage.getItem("email") || "",
        phone: localStorage.getItem("phone") || "",
        title: localStorage.getItem("userTitle") || ""
      });
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [propUserName]);

  const accountScope = useMemo(() => {
    if (isSuperAdmin) return "Platform-wide access";
    if (role === "admin") return "School administration";
    if (role === "headteacher") return "Academic leadership";
    return "Personal account";
  }, [isSuperAdmin, role]);
  const userCode = localStorage.getItem("userCode") || "Pending";

  const syncLocalProfile = (next: Partial<ProfileFormState> & { profilePicture?: string | null }) => {
    if (next.name !== undefined) localStorage.setItem("userName", next.name);
    if (next.email !== undefined) localStorage.setItem("email", next.email);
    if (next.phone !== undefined) localStorage.setItem("phone", next.phone);
    if (next.title !== undefined) localStorage.setItem("userTitle", next.title || "");
    if (next.profilePicture !== undefined) {
      if (next.profilePicture) {
        localStorage.setItem("profilePicture", next.profilePicture);
      } else {
        localStorage.removeItem("profilePicture");
      }
    }
    window.dispatchEvent(new Event("storage"));
  };

  const handleProfileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onloadstart = () => {
      setUploading(true);
      setMessage("");
      setError("");
    };

    reader.onloadend = async () => {
      const base64String = reader.result as string;
      const token = localStorage.getItem("token");
      if (!currentUserId || !token) {
        setUploading(false);
        return;
      }

      try {
        const response = await axios.put(
          getApiUrl(`users/${currentUserId}`),
          { profilePicture: base64String },
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );

        const updated = response.data?.user?.profilePicture || base64String;
        syncLocalProfile({ profilePicture: updated });
        setProfilePic(updated);
        setMessage("Profile photo updated successfully.");
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to upload profile photo.");
      } finally {
        setUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleProfileSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const token = localStorage.getItem("token");
    if (!currentUserId || !token) return;

    try {
      setSavingProfile(true);
      setMessage("");
      setError("");

      const payload = {
        name: profileForm.name.trim(),
        email: profileForm.email.trim(),
        phone: profileForm.phone.trim() || null,
        title: profileForm.title.trim() || null
      };

      const response = await axios.put(getApiUrl(`users/${currentUserId}`), payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const updatedUser = response.data?.user || {};
      syncLocalProfile({
        name: updatedUser.name || payload.name,
        email: updatedUser.email || payload.email,
        phone: payload.phone || "",
        title: payload.title || ""
      });
      setMessage("Account profile updated successfully.");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update account profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      setError("New password and confirmation do not match.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!currentUserId || !token) return;

    try {
      setPassLoading(true);
      setMessage("");
      setError("");

      await axios.put(
        getApiUrl(`users/${currentUserId}`),
        { password: passwords.new },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage("Password changed successfully.");
      setIsChangingPassword(false);
      setPasswords({ new: "", confirm: "" });
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to change password.");
    } finally {
      setPassLoading(false);
    }
  };

  if (hideWhenSet && profilePic !== getDefaultAvatarForTitle(localStorage.getItem("userTitle")) && profilePic !== GENERIC_AVATAR) {
    return null;
  }

  return (
    <div className="space-y-8 max-w-6xl">
      <div className="utopia-page-container p-10 bg-white">
        <div className="flex flex-col lg:flex-row gap-10 lg:items-start">
          <div className="relative group self-center lg:self-start">
            <div className="w-40 h-40 rounded-[2.5rem] bg-slate-50 border-4 border-white shadow-2xl overflow-hidden relative">
              <img
                src={profilePic}
                alt="Identity"
                className={`w-full h-full object-cover transition-all duration-500 ${uploading ? "scale-110 blur-sm opacity-50" : "group-hover:scale-105"}`}
              />
              {uploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-teal-600/10">
                  <Loader2 className="animate-spin text-teal-600" size={32} />
                </div>
              )}
              <label className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white">
                <Camera size={24} className="mb-2" />
                <span className="text-[10px] font-black uppercase tracking-widest">Update Photo</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleProfileUpload} disabled={uploading} />
              </label>
            </div>
            <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-teal-500 text-white rounded-2xl flex items-center justify-center shadow-lg border-4 border-white">
              <Fingerprint size={18} />
            </div>
          </div>

          <div className="flex-1 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                <h2 className="text-3xl font-black text-slate-800 tracking-tight">{profileForm.name}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <Mail size={14} />
                    <span className="text-xs font-bold uppercase tracking-widest">{profileForm.email || "identity@milestone.edu"}</span>
                  </div>
                  <span className="utopia-badge bg-teal-50 text-teal-600 border-teal-100 text-[9px] font-black uppercase px-3">
                    {localStorage.getItem("role")} Access
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-[280px]">
                <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Role</p>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{localStorage.getItem("role") || "User"}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Account Code</p>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{userCode}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Scope</p>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{accountScope}</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-slate-400 max-w-2xl">
              Manage your account identity, security credentials, and access profile. Changes here apply to your active portal account.
            </p>

            <div className="flex flex-wrap gap-3">
              <label className="utopia-btn-primary flex items-center gap-2 cursor-pointer">
                <Camera size={16} /> Choose New Photo
                <input type="file" accept="image/*" className="hidden" onChange={handleProfileUpload} disabled={uploading} />
              </label>
              {localStorage.getItem("profilePicture") && (
                <button
                  onClick={() => {
                    localStorage.removeItem("profilePicture");
                    setProfilePic(getDefaultAvatarForTitle(localStorage.getItem("userTitle")));
                    window.dispatchEvent(new Event("storage"));
                  }}
                  className="utopia-btn-secondary flex items-center gap-2 border-red-50 text-red-500 hover:bg-red-50"
                >
                  <Trash2 size={16} /> Reset Avatar
                </button>
              )}
            </div>
          </div>
        </div>

        {(message || error) && (
          <div className="mt-8 pt-8 border-t border-slate-50">
            {message && (
              <div className="p-4 bg-teal-50 border border-teal-100 rounded-2xl text-teal-600 text-xs font-bold flex items-center gap-2">
                <CheckCircle2 size={16} /> {message}
              </div>
            )}
            {error && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-xs font-bold flex items-center gap-2">
                <AlertCircle size={16} /> {error}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-8">
        <form onSubmit={handleProfileSave} className="utopia-page-container p-8 bg-white space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center shadow-inner">
              <UserCog size={20} />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-800 tracking-tight uppercase">Account Details</h3>
              <p className="text-sm text-slate-400">Update the identity information used across the portal.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Full Name</label>
              <input
                className="utopia-input"
                value={profileForm.name}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Title</label>
              <input
                className="utopia-input"
                value={profileForm.title}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Mr, Mrs, Dr, Principal"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Email Address</label>
              <input
                className="utopia-input"
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Phone Number</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="utopia-input pl-10"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="+260..."
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="submit" className="utopia-btn-primary inline-flex items-center gap-2" disabled={savingProfile}>
              {savingProfile && <Loader2 size={16} className="animate-spin" />}
              Save Profile
            </button>
          </div>
        </form>

        <div className="space-y-8">
          {canChangePassword && (
            <div className="utopia-page-container p-8 bg-white border-l-4 border-blue-500">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shadow-inner">
                  <Shield size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-800 tracking-tight uppercase">Security Credentials</h3>
                  <p className="text-sm text-slate-400">Rotate your password and keep this account secured.</p>
                </div>
              </div>

              {!isChangingPassword ? (
                <>
                  <p className="text-xs text-slate-400 mb-6 font-medium leading-relaxed">
                    Use a strong password with a mix of uppercase letters, lowercase letters, and numbers. Password changes apply immediately.
                  </p>
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="utopia-btn-secondary w-full flex items-center justify-center gap-2 font-black py-4 border-slate-100/50 hover:bg-slate-50 transition-all"
                  >
                    <Key size={16} /> Change Password
                  </button>
                </>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4 animate-in fade-in slide-in-from-top-2">
                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      placeholder="New Password"
                      required
                      value={passwords.new}
                      onChange={(e) => setPasswords((prev) => ({ ...prev, new: e.target.value }))}
                      className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 outline-none focus:bg-white focus:border-blue-500/30 transition-all"
                    />
                    <button type="button" onClick={() => setShowPass((prev) => !prev)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-blue-500">
                      <Key size={14} />
                    </button>
                  </div>
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder="Confirm New Password"
                    required
                    value={passwords.confirm}
                    onChange={(e) => setPasswords((prev) => ({ ...prev, confirm: e.target.value }))}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 outline-none focus:bg-white focus:border-blue-500/30 transition-all"
                  />
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setIsChangingPassword(false)}
                      className="flex-1 py-3 text-[10px] font-black uppercase text-slate-400 hover:text-slate-600 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={passLoading}
                      className="flex-[2] bg-blue-600 text-white rounded-[1.25rem] py-3 text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 shadow-lg shadow-blue-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {passLoading ? <Loader2 size={12} className="animate-spin" /> : <Shield size={12} />}
                      Save Password
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className="utopia-page-container p-8 bg-white border-l-4 border-indigo-500">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center shadow-inner">
                <User size={20} />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight uppercase">
                  {showAdminSettings ? "Administrative Access Profile" : "Account Access Profile"}
                </h3>
                <p className="text-sm text-slate-400">Quick account context for this login session.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Access Scope</p>
                <p className="font-semibold text-slate-800 mt-1">{accountScope}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Internal Record ID</p>
                <p className="font-semibold text-slate-800 mt-1">{currentUserId || "N/A"}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Email Routing</p>
                <p className="font-semibold text-slate-800 mt-1">{profileForm.email || "Not configured"}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Identity Title</p>
                <p className="font-semibold text-slate-800 mt-1">{profileForm.title || "Not set"}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Phone Routing</p>
                <p className="font-semibold text-slate-800 mt-1">{profileForm.phone || "Not configured"}</p>
              </div>
            </div>

            {isSuperAdmin && (
              <div className="mt-6 rounded-2xl border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-700">
                This account has platform-wide authority. Changes to your profile affect how you appear across school provisioning, support, and system administration workflows.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
